/**
 * Plain `column = value` WHERE conditions — the row drill's primary-key filter
 * (`buildDrillSql`). The results grid's "Add to WHERE" writes the filter chips'
 * condition instead (`addToWhere.ts`).
 */

import { safeToISOString } from '../../../../utils/dateUtils';

type EngineId = string;

/** Quote an identifier (column, table, schema name) if it contains special characters */
export function quoteIdentifier(name: string, engineId?: EngineId): string {
  const needsQuoting = /[^a-zA-Z0-9_]/.test(name) || /^\d/.test(name);
  if (!needsQuoting) return name;
  if (engineId === 'mssql') return `[${name.replace(/\]/g, ']]')}]`;
  if (engineId === 'mysql') return `\`${name.replace(/`/g, '``')}\``;
  return `"${name.replace(/"/g, '""')}"`;
}

/** Format a value for use in a WHERE clause */
export function formatWhereValue(value: unknown, engineId?: EngineId): string {
  if (value === null || value === undefined) return 'IS NULL';
  if (typeof value === 'number' || typeof value === 'bigint') return `= ${String(value)}`;
  if (typeof value === 'boolean') {
    if (engineId === 'mssql') return `= ${value ? '1' : '0'}`;
    if (engineId === 'pgsql' || engineId === 'mysql') return `= ${value ? 'TRUE' : 'FALSE'}`;
    return `= ${value ? '1' : '0'}`;
  }
  if (value instanceof Date) return `= '${safeToISOString(value)}'`;
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return `= '${json.replace(/'/g, "''")}'`;
  }
  return `= '${String(value).replace(/'/g, "''")}'`;
}

/** Build a complete WHERE condition string: `column = value` or `column IS NULL` */
export function buildWhereCondition(columnName: string, value: unknown, engineId?: EngineId): string {
  const quotedCol = quoteIdentifier(columnName, engineId);
  const formatted = formatWhereValue(value, engineId);
  return `${quotedCol} ${formatted}`;
}
